import api from './api';
import { toQueryDateYMD } from '../utils/formatters';

export interface NotaCreditoCompraDetalle {
  nroIdformulario: number;
  nroItem: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad?: number;
  precioLista?: number;
  porcDesc?: number;
  precioUnitario?: number;
  descuento?: number;
  subtotal?: number;
  tasa1?: number;
  tasa2?: number;
  tasa3?: number;
  tasa4?: number;
  tasa5?: number;
  impInterno1?: number;
  impInterno2?: number;
  tasaHidrica?: number;
  notas?: string;
  tipoProveedor?: string;
  nroCuenta?: number;
}

export interface ImpuestoPorFormularioReadDto {
  codigoImpuesto: string;
  montoBaseImp?: number;
  montoImpuesto?: number;
  ctaCtable?: number;
}

export interface NotaCreditoCompra {
  nroFormulario: number;
  fechaEmision: string | Date;
  fechaVencim?: string | Date;
  aplicacionIva?: string | Date;
  horaEmision?: string | Date;
  nroTipoFormulario?: string;
  tipoCliente?: string;
  tipoProveedor?: string;
  letra?: string;
  tipoIva?: string;
  nroSuc?: number;
  nroComprobante?: number;
  nroProveedor?: number;
  nombre?: string;
  nroLista?: string;
  codigoMoneda?: number | string;
  subtotal?: number;
  descuento?: number;
  importeSinIva?: number;
  exento?: number;
  ivainscripto?: number;
  ivanoInscripto?: number;
  ivaotro?: number;
  importeTotal?: number;
  asentado?: string;
  descripcion?: string;
  usuario?: string;
  cuit?: string;
  nroUbicacionDestino?: number;
  ubicacionDestinoDescripcion?: string;
  formaDePago?: string;
  formaEntrega?: string;
  ordenDeCompra?: string;
  /** Factura/ND de compra origen cuando la NC fue generada “desde factura”. */
  nroFormularioFactura?: number | null;
  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;
  percepciones?: number;
  impuestos?: ImpuestoPorFormularioReadDto[];
  detalles?: NotaCreditoCompraDetalle[];
}

export interface NotaCreditoCompraSearchParams {
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  fechaDesde?: Date | string;
  fechaHasta?: Date | string;
  nroProveedor?: string;
  asentado?: string;
  usuario?: string;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
}

export interface NotaCreditoCompraPagedResult {
  items: NotaCreditoCompra[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface NotaCreditoCompraFormularioCreateDto {
  idCompania: string;
  letra: string;
  nroSuc: number;
  nroComprobante: number;
  tipoProveedor: string;
  tipoComprobante: string;
  descripcion?: string;
  fechaEmision?: string;
  fechaVencim?: string;
  aplicacionIva?: string;
  nroProveedor: number;
  formaDePago?: string;
  nroUbicacionDestino?: number;
  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;
  nroFormularioFactura?: number;
  /** Total de IVA de cabecera; si se envía, el API reparte en columnas sin modificar tasas de ítems. */
  ivaManual?: number;
}

export interface NotaCreditoCompraItemCreateDto {
  codigoArticulo: string;
  descripcion?: string;
  cantidad: number;
  costoBrutoUnitario: number;
  porcDesc1?: number;
  porcDesc2?: number;
  porcDesc3?: number;
  porcDesc4?: number;
  porcDesc5?: number;
  um?: string;
  codigoMoneda?: number;
  cotizacion?: number;
  notas?: string;
  tasa1?: number;
  tasa2?: number;
  tasa3?: number;
  tasa4?: number;
  tasa5?: number;
  impInterno1?: number;
  impInterno2?: number;
  tasaHidrica?: number;
  tipoProveedor?: 'C' | 'G';
  nroCuenta?: number;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  lotes?: import('./loteService').LoteAsignacion[];
}

export interface ImpuestoPorFormularioCreateDto {
  codigoImpuesto: string;
  ctaCtable: number;
  montoBaseImp?: number;
  montoImpuesto?: number;
}

export interface NotaCreditoCompraCreatePayload {
  formulario: NotaCreditoCompraFormularioCreateDto;
  items: NotaCreditoCompraItemCreateDto[];
  impuestos?: ImpuestoPorFormularioCreateDto[];
}

const notaCreditoCompraService = {
  getNotasCreditoCompra: async (params: NotaCreditoCompraSearchParams): Promise<NotaCreditoCompraPagedResult> => {
    const queryParams: Record<string, string | number | undefined> = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      searchTerm: params.searchTerm || undefined,
      nroProveedor: params.nroProveedor || undefined,
      asentado: params.asentado || undefined,
      usuario: params.usuario || undefined,
      letra: params.letra || undefined,
      nroSuc: params.nroSuc ?? undefined,
      nroComprobante: params.nroComprobante ?? undefined
    };
    if (params.fechaDesde) {
      queryParams.fechaDesde = toQueryDateYMD(params.fechaDesde);
    }
    if (params.fechaHasta) {
      queryParams.fechaHasta = toQueryDateYMD(params.fechaHasta);
    }

    const response = await api.get<NotaCreditoCompraPagedResult>('/api/NotasCreditoCompra', { params: queryParams });
    return response.data;
  },

  getNotaCreditoCompra: async (id: number): Promise<NotaCreditoCompra> => {
    const response = await api.get<NotaCreditoCompra>(`/api/NotasCreditoCompra/${id}`);
    return response.data;
  },

  createNotaCreditoCompra: async (payload: NotaCreditoCompraCreatePayload): Promise<NotaCreditoCompra> => {
    const response = await api.post<NotaCreditoCompra>('/api/NotasCreditoCompra', payload);
    return response.data;
  },

  puedeAnularNotaCreditoCompra: async (id: number): Promise<{ puedeAnular: boolean; motivos: string[] }> => {
    const response = await api.get<{ puedeAnular: boolean; motivos: string[] }>(
      `/api/NotasCreditoCompra/${id}/puede-anular`
    );
    return response.data;
  },

  anularNotaCreditoCompra: async (id: number, dto?: { motivo?: string }): Promise<void> => {
    await api.post(`/api/NotasCreditoCompra/${id}/anular`, dto ?? {});
  }
};

export default notaCreditoCompraService;
